import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { Card, Button, WarningCallout, SummaryList } from 'nhsuk-react-components';
import { FaCheck, FaClock, FaCopy, FaExternalLinkAlt } from 'react-icons/fa';

function EmergencyAccessGrantedScreen() {
    const history = useHistory();
    const location = useLocation();
    const [tokenData, setTokenData] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [copySuccess, setCopySuccess] = useState(false);

    useEffect(() => {
        document.title = 'Emergency Access Granted';
        
        // Get token data from navigation state
        if (location.state && location.state.tokenData) {
            setTokenData(location.state.tokenData);
            
            // Calculate initial time remaining
            const expiresAt = new Date(location.state.tokenData.expiresAt);
            const now = new Date();
            const remaining = Math.floor((expiresAt - now) / 1000 / 60);
            setTimeRemaining(remaining);
        } else {
            // No token data, redirect to request page
            history.push('/emergency/request');
        }
    }, [location, history]);

    // Update countdown every minute
    useEffect(() => {
        if (!tokenData) return;

        const interval = setInterval(() => {
            const expiresAt = new Date(tokenData.expiresAt);
            const now = new Date();
            const remaining = Math.floor((expiresAt - now) / 1000 / 60);
            setTimeRemaining(remaining);

            if (remaining <= 0) {
                clearInterval(interval);
            }
        }, 60000); // Update every minute

        return () => clearInterval(interval);
    }, [tokenData]);

    const handleCopyToken = () => {
        if (tokenData && tokenData.token) {
            navigator.clipboard.writeText(tokenData.token);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 3000);
        }
    };

    const handleCopyUrl = () => {
        if (tokenData && tokenData.accessUrl) {
            navigator.clipboard.writeText(tokenData.accessUrl);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 3000);
        }
    };

    const handleViewPatient = () => {
        if (tokenData && tokenData.token) {
            history.push(`/emergency/secure/${tokenData.token}`);
        }
    };

    if (!tokenData) {
        return <div>Loading...</div>;
    }

    return (
        <div className="nhsuk-width-container">
            <main className="nhsuk-main-wrapper" id="maincontent" role="main">
                <Container fluid>
                    <div style={{ 
                        background: '#007f3b', 
                        color: 'white', 
                        padding: '24px', 
                        marginBottom: '32px',
                        borderRadius: '4px',
                        textAlign: 'center'
                    }}>
                        <FaCheck size={48} style={{ marginBottom: '16px' }} />
                        <h1 style={{ color: 'white', marginBottom: '8px' }}>Emergency Access Granted</h1>
                        <p style={{ fontSize: '1.2rem', marginBottom: '0' }}>
                            Access token generated successfully
                        </p>
                    </div>

                    {copySuccess && (
                        <div className="nhsuk-success-message" role="alert" style={{ marginBottom: '24px' }}>
                            <p>Copied to clipboard!</p>
                        </div>
                    )}

                    <Card>
                        <Card.Content>
                            <Card.Heading>Patient Information</Card.Heading>
                            <SummaryList>
                                <SummaryList.Row>
                                    <SummaryList.Key>Patient Name</SummaryList.Key>
                                    <SummaryList.Value>{tokenData.patientName}</SummaryList.Value>
                                </SummaryList.Row>
                                <SummaryList.Row>
                                    <SummaryList.Key>NHS Number</SummaryList.Key>
                                    <SummaryList.Value>{tokenData.patientNHSNumber}</SummaryList.Value>
                                </SummaryList.Row>
                            </SummaryList>
                        </Card.Content>
                    </Card>

                    <Card style={{ marginTop: '24px' }}>
                        <Card.Content>
                            <Card.Heading>Access Token</Card.Heading>
                            
                            <div style={{ 
                                background: '#f0f4f5', 
                                padding: '16px', 
                                borderRadius: '4px',
                                marginBottom: '16px'
                            }}>
                                <p style={{ marginBottom: '8px' }}>
                                    <strong>Token:</strong>
                                </p>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '16px'
                                }}>
                                    <code style={{ 
                                        flex: 1,
                                        background: 'white',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        wordBreak: 'break-all'
                                    }}>
                                        {tokenData.token}
                                    </code>
                                    <Button onClick={handleCopyToken} style={{ whiteSpace: 'nowrap' }}>
                                        <FaCopy /> Copy
                                    </Button>
                                </div>

                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    gap: '16px',
                                    marginTop: '16px',
                                    paddingTop: '16px',
                                    borderTop: '1px solid #d8dde0'
                                }}>
                                    <FaClock size={20} color="#005eb8" />
                                    <div>
                                        <p style={{ marginBottom: '4px', fontWeight: 'bold' }}>
                                            Time Remaining: {timeRemaining} minutes
                                        </p>
                                        <p style={{ 
                                            marginBottom: '0', 
                                            fontSize: '0.9rem',
                                            color: timeRemaining < 10 ? '#da291c' : '#4c6272'
                                        }}>
                                            {timeRemaining < 10 && 'Token will expire soon! '}
                                            Expires at: {new Date(tokenData.expiresAt).toLocaleString('en-GB')}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <WarningCallout>
                                <WarningCallout.Label>Important</WarningCallout.Label>
                                <ul>
                                    <li><strong>Single-use only:</strong> Token becomes invalid after viewing patient data</li>
                                    <li><strong>Time-limited:</strong> Valid for 1 hour from creation</li>
                                    <li><strong>Audited:</strong> All access is logged for security purposes</li>
                                </ul>
                            </WarningCallout>
                        </Card.Content>
                    </Card>

                    <div style={{ 
                        marginTop: '32px',
                        display: 'flex',
                        gap: '16px',
                        flexWrap: 'wrap'
                    }}>
                        <Button 
                            onClick={handleViewPatient}
                            style={{ minWidth: '200px' }}
                        >
                            <FaExternalLinkAlt /> View Patient Details
                        </Button>
                        
                        <Button 
                            onClick={handleCopyUrl}
                            secondary
                            style={{ minWidth: '200px' }}
                        >
                            <FaCopy /> Copy Access URL
                        </Button>

                        <Button 
                            onClick={() => history.push('/')}
                            secondary
                            style={{ minWidth: '200px' }}
                        >
                            Return to Home
                        </Button>
                    </div>
                </Container>
            </main>
        </div>
    );
}

export default EmergencyAccessGrantedScreen;



